import React, { useEffect, useState } from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import ColoredButton from 'components/ColoredButton';
import Loader from 'components/Loader';
import assetRopaApi from 'config/api/assetRopa/assetRopa';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';

type Props = {
  assetRopaId: number;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (processingActivityId: number) => void;
  open: boolean;
  organizationId?: number;
};

const AddProcessingActivityModal: React.FC<Props> = ({ assetRopaId, loading = false, onClose, onConfirm, open, organizationId }) => {
  const { t } = useTranslation();
  const messages = processing_activities_messages.asset_ropa.owner_workspace.add_processing_activity;
  const [selectedId, setSelectedId] = useState<number | ''>('');

  const availableQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_AVAILABLE_PROCESSING_ACTIVITIES, organizationId, assetRopaId],
    async () => {
      const { data } = await assetRopaApi.getAvailableProcessingActivities(organizationId as number, assetRopaId);
      return data;
    },
    {
      enabled: Boolean(open && organizationId && assetRopaId),
      retry: false,
    },
  );

  useEffect(() => {
    if (open) {
      setSelectedId('');
    }
  }, [open]);

  const options = availableQuery.data ?? [];

  const handleConfirm = () => {
    if (typeof selectedId !== 'number') return;
    onConfirm(selectedId);
  };

  const renderContent = () => {
    if (availableQuery.isLoading) {
      return <Loader inner />;
    }

    if (availableQuery.isError) {
      return t(processing_activities_messages.asset_ropa.errors.load_failed);
    }

    if (options.length === 0) {
      return t(messages.empty);
    }

    return (
      <FormControl fullWidth variant='outlined'>
        <InputLabel>{t(messages.select_label)}</InputLabel>
        <Select label={t(messages.select_label)} onChange={event => setSelectedId(event.target.value as number)} value={selectedId}>
          {options.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.name} ({option.processingRole})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <Dialog fullWidth maxWidth='sm' onClose={onClose} open={open}>
      <DialogTitle>{t(messages.title)}</DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='grey'
          disabled={loading}
          icon={false}
          onClick={onClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel)}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={loading || typeof selectedId !== 'number'}
          icon={false}
          onClick={handleConfirm}
          showLoader={loading}
          textLike={false}
          variant='contained'
        >
          {t(messages.confirm)}
        </ColoredButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddProcessingActivityModal;
